import React, { useState, useEffect } from "react";
import "./activityLog.css";
import { ArrowBigLeft, ArrowBigRight, ArrowLeft, ArrowRight, X } from "lucide-react";
import EventLogSkeleton from "./ActivitySkeleton";
import { fetchEmployeeHistoryUrl } from "../../../../api/endpoints";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";

const ActivityLog = ({ setShowEmployeeActivity, employee }) => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocumentCount, setTotalDocumentCount] = useState(0);
  const [activityData, setActivityData] = useState([]);

  const activities = [
    {
      id: 1,
      timestamp: "Nov 7",
      time: "11:27:27 PM",
      type: "LOGOUT",
      description: "User logged out of the system",
      location: "Aarhus, Central Jutland, Denmark",
      device: "Windows",
      ipAddress: "192.168.35.218",
    },
    {
      id: 2,
      timestamp: "Nov 7",
      time: "10:49:27 PM",
      type: "LOGOUT",
      description: "User logged out of the system",
      location: "Aalborg, North Denmark",
      device: "Windows",
      ipAddress: "192.168.149.192",
    },
    {
      id: 3,
      timestamp: "Nov 7",
      time: "10:27:27 PM",
      type: "ACCESS REVOKED",
      description: "Access revoked for security reasons",
      location: "Søborg, Capital Region, Denmark",
      device: "Windows",
      ipAddress: "192.168.248.24",
    },
    {
      id: 4,
      timestamp: "Nov 7",
      time: "09:39:17 PM",
      type: "LOGIN",
      description: "User logged into the system from Main Store",
      location: "Aalborg, North Denmark",
      device: "macOS",
      ipAddress: "192.168.216.150",
    },
    {
      id: 5,
      timestamp: "Nov 7",
      time: "08:27:28 PM",
      type: "ACCESS REVOKED",
      description: "Access revoked for security reasons",
      location: "Søborg, Capital Region, Denmark",
      device: "macOS",
      ipAddress: "192.168.106.102",
    },
    {
      id: 6,
      timestamp: "Nov 7",
      time: "08:21:24 PM",
      type: "ACCESS REVOKED",
      description: "Access revoked for security reasons",
      location: "Søborg, Capital Region, Denmark",
      device: "macOS",
      ipAddress: "192.168.136.237",
    },
    {
      id: 7,
      timestamp: "Nov 7",
      time: "08:13:10 PM",
      type: "ACCESS REVOKED",
      description: "Access revoked for security reasons",
      location: "Aalborg, North Denmark",
      device: "Windows",
      ipAddress: "192.168.186.233",
    },
    {
      id: 8,
      timestamp: "Nov 7",
      time: "06:25:06 PM",
      type: "PRODUCT UPDATE",
      description: "Product inventory updated at Mall Location",
      location: "Odense, Southern Denmark",
      device: "macOS",
      ipAddress: "192.168.117.221",
    },
    {
      id: 9,
      timestamp: "Nov 7",
      time: "04:54:15 PM",
      type: "SALES MADE",
      description: "Sales transaction of kr337,387 from Mall Location",
      location: "Aarhus, Central Jutland, Denmark",
      device: "macOS",
      ipAddress: "192.168.32.111",
    },
    {
      id: 10,
      timestamp: "Nov 7",
      time: "04:42:55 PM",
      type: "SALES MADE",
      description: "Sales transaction of kr261,550 from Main Store",
      location: "Copenhagen, Capital Region, Denmark",
      device: "Windows",
      ipAddress: "192.168.174.124",
    },
    {
      id: 11,
      timestamp: "Nov 7",
      time: "03:56:22 PM",
      type: "LOGIN",
      description: "User logged into the system from Mall Location",
      location: "Aarhus, Central Jutland, Denmark",
      device: "macOS",
      ipAddress: "192.168.118.99",
    },
    {
      id: 12,
      timestamp: "Nov 7",
      time: "03:13:37 PM",
      type: "LOGIN",
      description: "User logged into the system from Downtown Branch",
      location: "Odense, Southern Denmark",
      device: "macOS",
      ipAddress: "192.168.25.189",
    },
  ];

  useEffect(() => {
    // Simulate data fetching
    fetchActivities();
  }, [page]);

  const filters = ["All", "Login", "Product Update", "Failed Login Attempt", "Sales Made", "Sales Update"];

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", limit);
    return params.toString();
  };

  const fetchActivities = async () => {
    const query = buildQuery();
    console.log(query);

    try {
      setLoading(true);
      let response = await fetch(`${fetchEmployeeHistoryUrl}${employee.staffID}/?${query}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (response.status === 404) {
        setPage(0);
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch activities");
      }

      let { activityLogs, pagination } = await response.json();

      // Transform activity logs
      const transformedActivities = activityLogs.map((activity, index) => transformActivity(activity, index + 1));
      setTotalPages(pagination.totalPages);
      setTotalDocumentCount(pagination.totalDocuments);
      setPage(pagination.currentPage);

      setActivityData(transformedActivities);
    } catch (error) {
      uiAlert("Failed to fetch staff activities, try again later.", "error");
      console.error("Error fetching activities:", error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const getTypeClass = (type) => {
    const typeMap = {
      LOGOUT: "al-badge-logout",
      LOGIN: "al-badge-login",
      FAILEDLOGINATTEMPT: "al-badge-access-revoked",
      PRODUCTUPDATE: "al-badge-product-update",
      SALESMADE: "al-badge-sales-made",
    };
    return typeMap[type] || "al-badge-default";
  };

  const filteredActivities =
    activeFilter === "All"
      ? activityData
      : activityData.filter((activity) => activity.type.toLowerCase().replace(/\s+/g, "") === activeFilter.toLowerCase().replace(/\s+/g, ""));

  return (
    <>
      <div className='backDropActivuty'>
        {loading ? (
          <EventLogSkeleton setShowEmployeeActivity={setShowEmployeeActivity} />
        ) : (
          <div className='al-container'>
            <div className='al-header'>
              <div className='actBack-btn'>
                {" "}
                <span onClick={() => setShowEmployeeActivity(false)}>
                  <X />
                </span>{" "}
                <h1 className='al-title'>Activity Log</h1>
              </div>
              <div className='al-filters'>
                {filters.map((filter) => (
                  <button
                    key={filter}
                    className={`al-filter-btn ${activeFilter === filter ? "al-filter-btn-active" : ""}`}
                    onClick={() => setActiveFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            <div className='activity-log-pag-nav'>
              <span
                onClick={() => {
                  setPage((prev) => {
                    if (prev > 1) {
                      return parseInt(prev) - 1;
                    } else {
                      return prev;
                    }
                  });
                }}
              >
                <ArrowLeft size={18} />
              </span>
              <p className='al-showing-text'>
                Showing page {page} of {totalPages} of {totalDocumentCount} events
              </p>

              <span
                onClick={() => {
                  setPage((prev) => {
                    if (prev < totalPages) {
                      return parseInt(prev) + 1;
                    } else {
                      return prev;
                    }
                  });
                }}
              >
                <ArrowRight size={18} />
              </span>
            </div>

            {/* Desktop Table View */}
            <div className='al-table-container'>
              <table className='al-table'>
                <thead>
                  <tr className='al-table-header-row'>
                    <th className='al-table-header'>TIMESTAMP</th>
                    <th className='al-table-header'>TYPE</th>
                    <th className='al-table-header'>EVENT DESCRIPTION</th>
                    <th className='al-table-header'>LOCATION</th>

                    <th className='al-table-header'>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities?.map((activity) => (
                    <tr key={activity.id} className='al-table-row'>
                      <td className='al-td al-td-timestamp'>
                        <div className='al-timestamp-wrapper'>
                          <svg className='al-calendar-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                            <rect x='2' y='3' width='12' height='11' rx='1' stroke='currentColor' strokeWidth='1.5' />
                            <path d='M2 6h12' stroke='currentColor' strokeWidth='1.5' />
                            <path d='M5 1v3M11 1v3' stroke='currentColor' strokeWidth='1.5' />
                          </svg>
                          <div>
                            <div className='al-date'>{activity?.timestamp}</div>
                            <div className='al-time'>{activity?.time}</div>
                          </div>
                        </div>
                      </td>
                      <td className='al-td al-td-type'>
                        <span className={`al-badge ${getTypeClass(activity.type)}`}>{activity.type}</span>
                      </td>
                      <td style={{ textAlign: "left" }} className='al-td al-td-description'>
                        {activity.description}
                      </td>
                      <td style={{ textAlign: "left", fontSize: "0.75rem" }} className='al-td al-td-location'>
                        <div className='al-location-wrapper'>
                          <svg className='al-location-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                            <path d='M8 8.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z' stroke='currentColor' strokeWidth='1.5' />
                            <path d='M8 14s-4-3.5-4-7a4 4 0 118 0c0 3.5-4 7-4 7z' stroke='currentColor' strokeWidth='1.5' />
                          </svg>
                          <span>{activity.location}</span>
                        </div>
                      </td>
                      {/* <td className='al-td al-td-device'>
                      <svg className='al-device-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <rect x='2' y='3' width='12' height='9' rx='1' stroke='currentColor' strokeWidth='1.5' />
                        <path d='M5 13h6' stroke='currentColor' strokeWidth='1.5' />
                      </svg>
                      <span>{activity.device}</span>
                    </td> */}
                      <td className='al-td al-td-ip'>
                        {" "}
                        <div className='al-device-wrapper'>
                          <svg className='al-device-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                            <rect x='2' y='3' width='12' height='9' rx='1' stroke='currentColor' strokeWidth='1.5' />
                            <path d='M5 13h6' stroke='currentColor' strokeWidth='1.5' />
                          </svg>
                          <span>{activity.device}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className='al-card-container'>
              {filteredActivities.map((activity) => (
                <div key={activity.id} className='al-card'>
                  <div className='al-card-header'>
                    <div className='al-card-timestamp'>
                      <svg className='al-calendar-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <rect x='2' y='3' width='12' height='11' rx='1' stroke='currentColor' strokeWidth='1.5' />
                        <path d='M2 6h12' stroke='currentColor' strokeWidth='1.5' />
                        <path d='M5 1v3M11 1v3' stroke='currentColor' strokeWidth='1.5' />
                      </svg>
                      <div>
                        <span className='al-card-date'>{activity.timestamp}</span>
                        <span className='al-card-time'>{activity.time}</span>
                      </div>
                    </div>
                    <span className={`al-badge ${getTypeClass(activity.type)}`}>{activity.type}</span>
                  </div>

                  <p className='al-card-description'>{activity.description}</p>

                  <div className='al-card-details'>
                    <div className='al-card-detail-item'>
                      <svg className='al-location-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path d='M8 8.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z' stroke='currentColor' strokeWidth='1.5' />
                        <path d='M8 14s-4-3.5-4-7a4 4 0 118 0c0 3.5-4 7-4 7z' stroke='currentColor' strokeWidth='1.5' />
                      </svg>
                      <span>{activity.location}</span>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <div className='al-card-detail-item'>
                        <svg className='al-device-icon' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                          <rect x='2' y='3' width='12' height='9' rx='1' stroke='currentColor' strokeWidth='1.5' />
                          <path d='M5 13h6' stroke='currentColor' strokeWidth='1.5' />
                        </svg>
                        <span>{activity.device}</span>
                      </div>

                      <div className='al-card-detail-item'>
                        <span className='al-card-label'>IP:</span>
                        <span className='al-card-ip'>{activity.ipAddress}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ActivityLog;

function transformActivity(activity, index = 1) {
  const date = new Date(activity.activityDate || activity.createdAt);

  // Format date and time for display
  const timestamp = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // --- Detect device info from User-Agent ---
  const userAgent = activity.deviceInfo || "";
  let device = "Unknown";
  let isMobile = false;

  if (/windows/i.test(userAgent)) device = "Windows";
  else if (/mac os|macintosh/i.test(userAgent)) device = "Mac";
  else if (/iphone/i.test(userAgent)) {
    device = "iPhone";
    isMobile = true;
  } else if (/ipad/i.test(userAgent)) {
    device = "iPad";
    isMobile = true;
  } else if (/android/i.test(userAgent)) {
    device = "Android";
    isMobile = true;
  } else if (/linux/i.test(userAgent)) device = "Linux";

  // If no known device keywords but has "Mobile" -> assume mobile
  if (!isMobile && /mobile/i.test(userAgent)) isMobile = true;

  return {
    id: index,
    timestamp, // e.g. "Nov 12"
    time, // e.g. "10:49:32 PM"
    type: activity.activityType?.toUpperCase(), // e.g. "LOGIN"
    description: activity.activityDescription,
    location: activity.location || "Unknown",
    device,
    platform: isMobile ? "Mobile" : "Desktop", // new field
    ipAddress: activity.ipAddress || "N/A",
  };
}
